import apiClient from "@shared/config/axiosConfig";
import imageCacheService from "@shared/lib/imageCache";

/**
 * Verifica si una URL de S3 ha expirado haciendo una petición HEAD
 * @param {string} url - URL a verificar
 * @returns {Promise<boolean>} - true si la URL es válida, false si ha expirado
 */
const isUrlStillValid = async (url) => {
  if (!url) return false;

  try {
    // Verificar si la URL tiene parámetros de expiración de S3
    const urlObj = new URL(url);
    const amzDate = urlObj.searchParams.get("X-Amz-Date");
    const amzExpires = urlObj.searchParams.get("X-Amz-Expires");

    if (amzDate && amzExpires) {
      // Parsear fecha de firma (formato: 20260403T185031Z)
      const signed = new Date(
        amzDate.replace(
          /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
          "$1-$2-$3T$4:$5:$6Z",
        ),
      );
      const expiresMs = signed.getTime() + Number(amzExpires) * 1000;
      // Considerar expirada si le quedan menos de 5 minutos
      return Date.now() < expiresMs - 5 * 60 * 1000;
    }

    // Sin parámetros de S3, asumir válida
    return true;
  } catch (error) {
    console.warn(
      `🔍 URL parece inválida o expirada: ${url.substring(0, 50)}...`,
    );
    return false;
  }
};

/**
 * Obtiene una imagen con manejo inteligente de expiración de URLs
 * @param {string} imagePath - Ruta del archivo en formato "folder/filename"
 * @returns {Promise<string>} - URL válida del archivo
 */
const getImage = async (imagePath) => {
  if (!imagePath) {
    console.warn("getImage: imagePath no proporcionado");
    return null;
  }

  try {
    // Intentar obtener la URL del caché primero
    let cachedUrl = imageCacheService.getFromCache(imagePath);

    if (cachedUrl) {
      // Verificar si la URL en caché sigue siendo válida
      const isValid = await isUrlStillValid(cachedUrl);
      if (isValid) {
        return cachedUrl;
      } else {
        console.warn(
          `âš ï¸ URL del caché ha expirado, eliminando y renovando: ${imagePath}`,
        );
        imageCacheService.removeFromCache(imagePath);
      }
    }

    // Verificar si ya hay una petición en vuelo para esta imagen
    if (imageCacheService.isPendingRequest(imagePath)) {
      return await imageCacheService.getPendingRequest(imagePath);
    }

    // Si no está en caché o ha expirado, hacer petición a la API
    const slashIdx = imagePath.indexOf("/");
    const folder = imagePath.slice(0, slashIdx);
    const file = imagePath.slice(slashIdx + 1);

    // Crear la promesa de la petición con timeout aumentado.
    // encodeURIComponent es imprescindible: nombres con '#', '?', '&' o espacios
    // (p. ej. "Bomba diésel #1.pdf") rompen la URL sin codificar y el backend
    // recibe una clave truncada → NoSuchKey en S3.
    const requestPromise = apiClient
      .get(
        `/files/${encodeURIComponent(file)}?folder=${encodeURIComponent(folder)}`,
        {
          timeout: 30000, // 30 segundos de timeout
        },
      )
      .then((response) => {
        const newUrl = response.data.url;
        // Almacenar la nueva URL en el caché
        imageCacheService.setInCache(imagePath, newUrl);
        return newUrl;
      })
      .catch((error) => {
        console.error(
          `âŒ Error en petición API para ${imagePath}:`,
          error.message,
        );
        throw error;
      });

    // Almacenar la promesa en vuelo
    imageCacheService.setPendingRequest(imagePath, requestPromise);

    return await requestPromise;
  } catch (error) {
    console.error(`âŒ Error obteniendo imagen ${imagePath}:`, error);

    // En caso de error de red, intentar devolver URL cacheada aunque esté expirada
    if (
      error.code === "ECONNABORTED" ||
      error.message.includes("timeout") ||
      error.code === "NETWORK_ERROR" ||
      !navigator.onLine
    ) {
      const cacheKey = imageCacheService.generateCacheKey(imagePath);
      const expiredEntry = imageCacheService.cache[cacheKey];
      if (expiredEntry && expiredEntry.url) {
        console.warn(
          `âš ï¸ Error de red - devolviendo URL expirada del caché para: ${imagePath}`,
        );
        return expiredEntry.url;
      }
    }

    // Para otros errores, no devolver URL expirada
    throw error;
  }
};

const uploadFile = async (
  file,
  folder,
  customFileName = null,
  { convertPdfToSvg = false } = {},
) => {
  try {
    // Validaciones previas
    if (!file) {
      throw new Error("Archivo requerido");
    }

    if (!folder || folder.trim() === "") {
      throw new Error("Carpeta requerida");
    }

    // Validar tamaño del archivo (máximo 100MB)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      throw new Error("El archivo excede el tamaño máximo permitido (100MB)");
    }

    // Validar tipos de archivo
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
      "image/svg+xml",
    ];
    if (!allowedTypes.includes(file.type)) {
      throw new Error(
        `Tipo de archivo no permitido. Tipos permitidos: ${allowedTypes.join(
          ", ",
        )}`,
      );
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", folder);

    // Si se proporciona un nombre personalizado, añadirlo al FormData
    if (customFileName) {
      formData.append("customFileName", customFileName);
    }
    if (convertPdfToSvg) formData.append("convertPdfToSvg", "true");

    // Configuración optimizada para subida de archivos
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 300000, // 5 minutos para archivos grandes
    };

    const response = await apiClient.post("/files", formData, config);

    return response.data;
  } catch (error) {
    console.error("âŒ Error detallado al subir archivo:", error);

    // Manejar errores específicos
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      throw new Error(
        "La subida del archivo tardó demasiado tiempo. Verifica tu conexión e intenta de nuevo.",
      );
    } else if (error.response?.status === 413) {
      throw new Error("El archivo es demasiado grande para el servidor.");
    } else if (error.response?.status === 400) {
      throw new Error(
        error.response.data?.error || "Error de validación del archivo.",
      );
    } else if (error.response?.status >= 500) {
      throw new Error(
        "Error del servidor. Por favor, intenta de nuevo más tarde.",
      );
    } else if (
      error.message.includes("Archivo requerido") ||
      error.message.includes("Carpeta requerida") ||
      error.message.includes("excede el tamaño") ||
      error.message.includes("no permitido")
    ) {
      throw error; // Re-lanzar errores de validación tal como están
    } else {
      throw new Error(`Error al subir archivo: ${error.message}`);
    }
  }
};

/**
 * Obtiene múltiples imágenes de manera optimizada usando caché
 * @param {Array} imagePaths - Array de rutas de imágenes
 * @param {Object} options - Opciones de configuración
 * @returns {Array} Array de objetos con imagePath y url
 */
const getMultipleImages = async (imagePaths, options = {}) => {
  const {
    batchSize = 5, // Procesar en lotes de 5
    retryFailures = true,
  } = options;

  if (!Array.isArray(imagePaths) || imagePaths.length === 0) {
    return [];
  }

  // Eliminar duplicados de las rutas de entrada
  const uniquePaths = [...new Set(imagePaths.filter((path) => path))];

  const results = [];
  const failures = [];

  // Procesar en lotes para evitar sobrecarga
  for (let i = 0; i < uniquePaths.length; i += batchSize) {
    const batch = uniquePaths.slice(i, i + batchSize);

    const batchPromises = batch.map(async (imagePath) => {
      try {
        const url = await getImage(imagePath);
        return { imagePath, url, success: true };
      } catch (error) {
        console.error(`Error procesando imagen ${imagePath}:`, error);
        failures.push(imagePath);
        return { imagePath, url: null, success: false, error };
      }
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);

    // Pequeña pausa entre lotes para no saturar
    if (i + batchSize < uniquePaths.length) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  // Reintentar fallidos si está habilitado
  if (retryFailures && failures.length > 0) {
    for (const failedPath of failures) {
      try {
        await new Promise((resolve) => setTimeout(resolve, 500)); // Espera más larga
        const url = await getImage(failedPath);
        const failedIndex = results.findIndex(
          (r) => r.imagePath === failedPath && !r.success,
        );
        if (failedIndex !== -1) {
          results[failedIndex] = { imagePath: failedPath, url, success: true };
        }
      } catch (error) {
        console.error(`Reintento fallido para ${failedPath}:`, error);
      }
    }
  }

  return results;
};

/**
 * Precarga imágenes en el caché sin devolverlas inmediatamente
 * Íštil para precargar imágenes que se usarán pronto
 */
const preloadImages = async (imagePaths, options = {}) => {
  // Ejecutar en segundo plano sin bloquear
  setTimeout(async () => {
    try {
      await getMultipleImages(imagePaths, { ...options, batchSize: 3 });
    } catch (error) {
      console.error("Error en precarga de imágenes:", error);
    }
  }, 100);
};

/**
 * Limpia el caché de imágenes - función de utilidad
 */
const clearImageCache = () => {
  imageCacheService.clearAllCache();
};

/**
 * Obtiene estadísticas del caché de imágenes
 */
const getCacheStats = () => {
  return imageCacheService.getCacheStats();
};

/**
 * Fuerza la renovación de una URL específica
 * Íštil cuando se detecta que una URL ha expirado durante su uso
 * @param {string} imagePath - Ruta del archivo
 * @returns {Promise<string>} - Nueva URL válida
 */
const refreshImageUrl = async (imagePath) => {
  if (!imagePath) {
    console.warn("refreshImageUrl: imagePath no proporcionado");
    return null;
  }

  // Eliminar del caché
  imageCacheService.removeFromCache(imagePath);

  // Limpiar peticiones pendientes
  imageCacheService.clearPendingRequest(imagePath);

  // Obtener nueva URL
  return await getImage(imagePath);
};

/**
 * Verifica si una respuesta de error indica que la URL ha expirado
 * @param {string} errorText - Texto del error
 * @returns {boolean} - true si indica expiración de URL
 */
const isS3UrlExpiredError = (errorText) => {
  if (!errorText || typeof errorText !== "string") return false;

  return (
    errorText.includes("<Code>AccessDenied</Code>") &&
    errorText.includes("<Message>Request has expired</Message>")
  );
};

const fileService = {
  getImage,
  refreshImageUrl,
  isS3UrlExpiredError,
  uploadFile,
  getMultipleImages,
  preloadImages,
  clearImageCache,
  getCacheStats,
};

export default fileService;
